import DOMPurify from 'dompurify';
import CryptoJS from 'crypto-js';

// Secret key for encryption - in a real app, this should be more secure
// or derived from a user-specific value if possible.
const ENCRYPTION_KEY = import.meta.env.VITE_STORAGE_ENCRYPTION_KEY || 'smartaccount-erp-secure-key-2024';

/**
 * Sanitizes HTML to prevent XSS attacks
 */
export const sanitizeHTML = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'div', 'span', 
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'h1', 'h2', 
      'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'img', 'style'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style', 'width', 'height', 'colspan', 'rowspan'],
  });
};

/**
 * Encrypts data using AES
 */
export const encrypt = (data: string): string => {
  try {
    return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error('Encryption failed:', error);
    return data;
  }
};

/**
 * Decrypts data using AES
 */
export const decrypt = (ciphertext: string): string => {
  if (!ciphertext || typeof ciphertext !== 'string') return ciphertext;
  
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, ENCRYPTION_KEY);
    const originalText = bytes.toString(CryptoJS.enc.Utf8);
    
    // If originalText is empty but ciphertext was not, it's likely plain text
    // or encrypted with a different key. We return ciphertext as fallback.
    return originalText || ciphertext;
  } catch (error) {
    // Silent fallback for legacy/plain-text data during migration
    return ciphertext;
  }
};

/**
 * List of sensitive keys that should be encrypted in storage
 */
export const SECURE_STORAGE_KEYS = [
  'token',
  'auth-sync',
  'googleDriveAccessToken',
  'googleDriveTokenExpiry',
  'googleDriveApiKey',
  'googleDriveClientId',
  'googleDriveFolderId',
  'deviceId',
  'rememberedUsername',
  'apiBaseUrl',
  'notionAccessToken',
  'sallaAccessToken',
  'jahezApiKey',
  'offline_invoices_queue',
  'licenseValidationCache'
];
